import {log, Models, Processes} from "@hantera/portal-app";
import parseJson from "@/parse-json";

export type WebSocketMessage =
  GraphMetaModelUpdatedMessage |
  UserSettingsUpdatedMessage;

export interface GraphMetaModelUpdatedMessage {
  type: 'graphMetaModelUpdated'
  nodes: Record<string, Models.GraphMeta.GraphNodeMeta>
}

export interface UserSettingsUpdatedMessage {
  type: 'userSettingsUpdated'
  values: Record<string, any>
}

export class PortalWebSocketClient {
  private readonly url: string;
  private socket: WebSocket | null = null;
  private reconnectInterval: number = 1000;
  private reconnectAttempts: number = 0;
  
  private subscriptions = new Set<(message: WebSocketMessage) => void>();

  private internalClientId?: string;
  
  public get clientId() {
    return this.internalClientId;
  }
  
  constructor(
    serverHost: string,
    private onopen: () => void,
    private processes: Processes
  ) {
    this.url = `wss://${serverHost}/ws`;
    this.connect();
  }

  private connect(): void {
    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      log.debug('WebSocket connected');
      this.reconnectAttempts = 0;
      this.onopen();
    };

    this.socket.onmessage = (event) => {
      const message = parseJson(event.data);

      log.debug('Message from server:', message.type);
      if (message.type === 'error') {
        this.processes.error({
          message: 'Hantera reported error: ' + message.code
        });
        return;
      }
      if (message.type === 'clientId') {
        this.internalClientId = message.id;
        return;
      }
      
      for (const subscription of this.subscriptions) {
        subscription(message);
      }
    };

    this.socket.onclose = (event) => {
      log.debug('WebSocket closed:', event);
      setTimeout(() => this.reconnect(), 1000);
    };

    this.socket.onerror = (error) => {
      log.debug('WebSocket error:', error);
    };
  }
  
  public subscribe(handler: (message: WebSocketMessage) => void) {
    this.subscriptions.add(handler);
  }
  public unsubscribe(handler: (message: WebSocketMessage) => void) {
    log.debug('Unsubscribed: ', this.subscriptions.delete(handler));
  }

  private reconnect(): void {
    setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.reconnectAttempts++;
      this.connect();
    }, Math.min(60_000, this.reconnectInterval * Math.max(1, this.reconnectAttempts)));
  }

  public isOpen() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
  
  public send(data: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      console.error('WebSocket is not open. Cannot send data.');
    }
  }

  public close(): void {
    if (this.socket) {
      this.socket.close();
    }
  }
}
